import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faBookOpen,
  faPlayCircle,
  faTachometerAlt,
  faHistory,
  faInfoCircle,
  faStar,
  faFilter,
  faChartBar,
  faWeightHanging,
  faDesktop,
  faChartPie,
  faTable,
  faTags,
  faCogs,
  faFileExport,
  faBell,
  faChartLine,
  faCalculator,
  faWrench,
  faQuestionCircle,
  faHeadset,
  faEnvelope,
  faPhone,
  faComments,
  faFileAlt,
  faChevronDown,
  faUserCircle,
  faCaretDown,
  faUser,
  faSignOutAlt,
  faCrown,
  faSyncAlt,
  faCheckCircle,
  faExclamationCircle
} from '@fortawesome/free-solid-svg-icons';
import '../scss/HelpCenter.scss';
import BaseURL from '../assets/contants/BaseURL';

const Help = () => {
  const navigate = useNavigate();
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [openFaqs, setOpenFaqs] = useState({});
  const [notification, setNotification] = useState(null);
  const [orgLogo, setOrgLogo] = useState(null);

  const showNotification = (message, type = 'success') => {
    setNotification({ message, type });
    setTimeout(() => setNotification(null), 3000);
  };

  const toggleFaq = (index) => {
    setOpenFaqs(prev => ({ ...prev, [index]: !prev[index] }));
  };

  const handleLogout = () => {
    if (window.confirm('Are you sure you want to logout?')) {
      localStorage.removeItem('isLoggedIn');
      showNotification('Logged out successfully!', 'success');
      setTimeout(() => navigate('/login'), 1500);
    }
  };

  const getAuthToken = () => sessionStorage.getItem('Token') || localStorage.getItem('Token');

  useEffect(() => {
      const fetchOrgLogo = async () => {
        try {
          const token = getAuthToken();
          const organizationId = sessionStorage.getItem('organization_id');
  
          const response = await fetch(`${BaseURL}Userauth/organizations/${organizationId}/`, {
            headers: {
              'Authorization': `Token ${token}`,
              'Content-Type': 'application/json',
            }
          });
  
          if (response.ok) {
            const data = await response.json();
            if (data.data?.logo_url) { 
              setOrgLogo(data.data.logo_url);
            }
          }
        } catch (error) {
          console.error('Error fetching org logo:', error);
        }
      };
  
      fetchOrgLogo();
    }, []);

  const faqItems = [
    {
      question: 'How do I set up custom alerts for vibration monitoring?',
      answer: (
        <>
          <p>Navigate to the Alerts page from the sidebar. Click "Create Alert" and select "Vibration" as the parameter type. You can set thresholds for:</p>
          <ul>
            <li>Overall vibration levels (mm/s)</li>
            <li>Specific frequency ranges (Hz)</li>
            <li>Individual axes (X, Y, Z)</li>
          </ul>
          <p>Choose notification methods (email, SMS, or in-app) and save your alert.</p>
        </>
      )
    },
    {
      question: 'What do the different vibration status colors mean?',
      answer: (
        <>
          <p>The system uses color coding to indicate vibration severity:</p>
          <ul>
            <li><strong>Green (Normal):</strong> Vibration levels are within safe operating limits</li>
            <li><strong>Yellow (Warning):</strong> Vibration approaching dangerous levels - monitor closely</li>
            <li><strong>Red (Critical):</strong> Immediate attention required - potential equipment damage</li>
            <li><strong>Gray (Offline):</strong> Sensor is not reporting data</li>
          </ul>
        </>
      )
    },
    {
      question: 'How often is vibration data updated?',
      answer: (
        <>
          <p>Vibration data is collected and transmitted in real-time:</p>
          <ul>
            <li><strong>Live monitoring:</strong> Updates every 2 seconds</li>
            <li><strong>Trend charts:</strong> 1-minute averages</li>
            <li><strong>Historical data:</strong> Stored at 5-minute intervals</li>
            <li><strong>Spectrum analysis:</strong> Updated every 30 seconds</li>
          </ul>
        </>
      )
    },
    {
      question: 'Can I export vibration data for analysis?',
      answer: (
        <>
          <p>Yes! You can export vibration data in multiple formats:</p>
          <ul>
            <li><strong>CSV:</strong> Raw time-series data for all axes</li>
            <li><strong>Excel:</strong> Formatted reports with charts</li>
            <li><strong>PDF:</strong> Professional reports for documentation</li>
            <li><strong>API:</strong> Direct integration with other systems (Pro plan)</li>
          </ul>
          <p>Go to the Reports page to generate and download your data.</p>
        </>
      )
    },
    {
      question: 'How far back does the operations log store data?',
      answer: (
        <>
          <p>Data retention depends on your plan:</p>
          <ul>
            <li><strong>Basic:</strong> 30 days of operational data</li>
            <li><strong>Advanced:</strong> 6 months of operational data</li>
            <li><strong>Pro:</strong> Unlimited data retention</li>
          </ul>
        </>
      )
    },
    {
      question: 'Can I track multiple cranes simultaneously?',
      answer: (
        <>
          <p>Yes! The system supports monitoring of multiple cranes:</p>
          <ul>
            <li>Each crane is identified by its unique ID</li>
            <li>Filters allow you to view individual cranes or all at once</li>
            <li>Reports can be generated for specific cranes or groups</li>
          </ul>
        </>
      )
    },
    {
      question: 'How accurate are the operation duration measurements?',
      answer: (
        <>
          <p>Duration measurements are highly accurate:</p>
          <ul>
            <li>Recorded to the nearest second</li>
            <li>Based on sensor activation timestamps</li>
            <li>Automatically adjusted for any system latency</li>
          </ul>
        </>
      )
    }
  ];

  const contactMethods = [
    { icon: faEnvelope, title: 'Email Support', detail: 'support@craneiq.com', description: 'Typically responds within 2 business hours' },
    { icon: faPhone, title: 'Phone Support', detail: '+1 (800) 555-0199', description: '24/7 for critical issues' },
    { icon: faComments, title: 'Live Chat', detail: 'Available in-app', description: 'Mon-Fri, 9am-5pm EST' },
    { icon: faFileAlt, title: 'Knowledge Base', detail: 'docs.craneiq.com', description: 'Detailed guides and tutorials' }
  ];

  const featureCards = [
    { icon: faTachometerAlt, title: 'Real-time Tracking', description: 'Monitor all crane operations as they happen with timestamped records.' },
    { icon: faFilter, title: 'Advanced Filtering', description: 'Filter by crane, operation type, and date range for focused analysis.' },
    { icon: faChartBar, title: 'Metrics Dashboard', description: 'Quick-view summary of operation counts and durations.' },
    { icon: faWeightHanging, title: 'Load Tracking', description: 'Record and analyze load weights for each operation.' }
  ];

  const advancedFeatures = [
    { icon: faFileExport, title: 'Data Export', description: 'Export operation logs to CSV, Excel, or PDF for reporting', code: 'Reports → Export Operations Log' },
    { icon: faBell, title: 'Custom Alerts', description: 'Set notifications for unusual operation patterns', code: 'Alerts → New Alert → Operation Parameters' },
    { icon: faChartLine, title: 'Trend Analysis', description: 'View operational trends over time', code: 'Reports → Operation Trends' },
    { icon: faCalculator, title: 'Utilization Reports', description: 'Calculate equipment utilization rates', code: 'Reports → Utilization Analysis' }
  ];

  const troubleshootingItems = [
    {
      title: 'Missing operation records',
      solutions: [
        "Verify the crane's sensors are properly connected",
        "Check your date range filters aren't too restrictive",
        "Confirm the crane was operational during the selected period"
      ]
    },
    {
      title: 'Incorrect operation types',
      solutions: [
        "Check sensor calibration on the crane",
        "Verify the crane configuration in Settings",
        "Contact support if issues persist"
      ]
    }
  ];

  const operationTypes = [
    { type: 'Hoist Up', class: 'type-hoist-up', description: 'Lifting operations' },
    { type: 'Hoist Down', class: 'type-hoist-down', description: 'Lowering operations' },
    { type: 'CT Left', class: 'type-ct-left', description: 'Cross travel to left' },
    { type: 'CT Right', class: 'type-ct-right', description: 'Cross travel to right' },
    { type: 'LT Forward', class: 'type-lt-forward', description: 'Long travel forward' },
    { type: 'LT Reverse', class: 'type-lt-reverse', description: 'Long travel reverse' },
    { type: 'Switch', class: 'type-switch', description: 'Mode changes' }
  ];

  const tableColumns = [
    { name: 'Timestamp', description: 'Exact date and time of operation', example: '2023-06-15 08:23:45' },
    { name: 'Crane ID', description: 'Identifier for the specific crane', example: 'CRN-001' },
    { name: 'Operation', description: 'Type of movement with color coding', example: 'Hoist Up' },
    { name: 'Duration', description: 'How long the operation took', example: '2:15 (minutes:seconds)' },
    { name: 'Load', description: 'Weight being handled', example: '3,250 kg' }
  ];

  return (
    <div className="help-center-container">
      <div className="dashboard-content">
        {/* Header */}
        <div className="header">
          <div className="header-left">
            <div className="header-logo">CraneIQ</div>
          </div>

          <div className="user-info-wrapper">
                      <div className="account-dropdown">
                        <div className="account-trigger" onClick={() => setIsDropdownOpen(!isDropdownOpen)}>
                          {orgLogo ? (
                                            <img
                                              src={orgLogo}
                                              alt="Organization Logo"
                                              style={{
                                                width: '32px',
                                                height: '32px',
                                                borderRadius: '50%',
                                                objectFit: 'cover',
                                                border: '2px solid #4361ee'
                                              }}
                                              onError={() => setOrgLogo(null)}
                                            />
                                          ) : (
                                            <FontAwesomeIcon icon={faUserCircle} style={{ fontSize: '1.5rem', color: '#4361ee' }} />
                                          )}
                          <span>{sessionStorage.getItem('username') || 'User'}</span>
                          <FontAwesomeIcon icon={faCaretDown} />
                        </div>
                        {isDropdownOpen && (
                          <div className="account-menu show">
                            <div className="account-menu-item plan-info">
                              <FontAwesomeIcon icon={faCrown} style={{ color: 'gold' }} />
                              <span>Basic Plan</span>
                            </div>
                            <div className="account-menu-divider"></div>
                            <button className="account-menu-item" style={{ background: 'none', border: 'none', width: '100%', textAlign: 'left', cursor: 'pointer' }}>
                              <FontAwesomeIcon icon={faUser} /> Profile
                            </button>
                            <button className="account-menu-item" style={{ background: 'none', border: 'none', width: '100%', textAlign: 'left', cursor: 'pointer' }}>
                              <FontAwesomeIcon icon={faSyncAlt} /> Upgrade Plan
                            </button>
                            <div className="account-menu-divider"></div>
                            <button className="account-menu-item logout-account-btn" onClick={handleLogout}>
                              <FontAwesomeIcon icon={faSignOutAlt} /> Logout
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
        </div>

        {/* Page Title */}
        <div className="page-title">
          <h1>Help Center</h1>
          <p>Find answers and resources for the CraneIQ Monitoring System</p>
        </div>

        {/* Help Content */}
        <div className="help-container">
          {/* Getting Started Section */}
          <h2 className="section-title">
            <FontAwesomeIcon icon={faBookOpen} /> Getting Started
          </h2>

          <div className="help-card">
            <h3 className="card-title">
              <FontAwesomeIcon icon={faPlayCircle} /> System Overview
            </h3>
            <div className="card-content">
              <p>CraneIQ is an advanced monitoring system designed to provide real-time insights into your crane operations. The system tracks multiple parameters to ensure optimal performance and safety.</p>
              <p>Key features include:</p>
              <ul>
                <li>Real-time monitoring of load, vibration, temperature, and energy</li>
                <li>Historical data analysis with customizable reports</li>
                <li>Automated alerts for abnormal conditions</li>
                <li>Predictive maintenance recommendations</li>
                <li>Multi-user access with role-based permissions</li>
              </ul>
            </div>
          </div>

          <div className="help-card">
            <h3 className="card-title">
              <FontAwesomeIcon icon={faTachometerAlt} /> Dashboard Guide
            </h3>
            <div className="card-content">
              <p>The dashboard provides a comprehensive overview of your crane's status:</p>
              <ul>
                <li><strong>Status Cards:</strong> Quick view of each crane's current state</li>
                <li><strong>OEE Metrics:</strong> Overall Equipment Effectiveness scores</li>
                <li><strong>Quick Stats:</strong> Power consumption, current draw, and utilization</li>
                <li><strong>Filter Bar:</strong> Quickly sort cranes by status (Working, Idle, Off, etc.)</li>
              </ul>
              <p>Click on any crane card to view detailed metrics and historical data.</p>
            </div>
          </div>

          {/* Operations Log Documentation Section */}
          <div className="help-section">
            <h2 className="section-title">
              <FontAwesomeIcon icon={faHistory} /> Operations Log Documentation
            </h2>

            <div className="help-card">
              <h3 className="card-title">
                <FontAwesomeIcon icon={faInfoCircle} /> Overview
              </h3>
              <div className="card-content">
                <p>The Operations Log provides a complete record of all crane operations, including hoisting, traveling, and switching activities. This feature helps you track equipment usage, analyze operational patterns, and maintain compliance with safety regulations.</p>
              </div>
            </div>

            <div className="help-card">
              <h3 className="card-title">
                <FontAwesomeIcon icon={faStar} /> Key Features
              </h3>
              <div className="card-content">
                <div className="feature-grid">
                  {featureCards.map((feature, index) => (
                    <div className="feature-card" key={index}>
                      <div className="feature-icon">
                        <FontAwesomeIcon icon={feature.icon} />
                      </div>
                      <h4>{feature.title}</h4>
                      <p>{feature.description}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <div className="help-card">
              <h3 className="card-title">
                <FontAwesomeIcon icon={faDesktop} /> Understanding the Interface
              </h3>
              <div className="card-content">
                <div className="interface-guide">
                  <div className="guide-item">
                    <h4><FontAwesomeIcon icon={faChartPie} /> Metrics Dashboard</h4>
                    <p>The compact dashboard at the top provides an overview of recent activity:</p>
                    <ul>
                      <li><strong>Hoist Operations:</strong> Total lifts (up/down breakdown)</li>
                      <li><strong>CT (Cross Travel):</strong> Left/right movements</li>
                      <li><strong>LT (Long Travel):</strong> Forward/reverse movements</li>
                      <li><strong>Switching:</strong> Mode changes</li>
                      <li><strong>Duration:</strong> Total operating time</li>
                      <li><strong>Load:</strong> Total weight handled</li>
                    </ul>
                  </div>

                  <div className="guide-item">
                    <h4><FontAwesomeIcon icon={faTable} /> Operations Table</h4>
                    <p>The main table displays detailed records of each operation:</p>
                    <table className="help-table">
                      <thead>
                        <tr>
                          <th>Column</th>
                          <th>Description</th>
                          <th>Example</th>
                        </tr>
                      </thead>
                      <tbody>
                        {tableColumns.map((col, index) => (
                          <tr key={index}>
                            <td>{col.name}</td>
                            <td>{col.description}</td>
                            <td>{col.example}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  <div className="guide-item">
                    <h4><FontAwesomeIcon icon={faTags} /> Operation Type Colors</h4>
                    <div className="color-legend">
                      {operationTypes.map((op, index) => (
                        <div className="legend-item" key={index}>
                          <span className={`operation-type ${op.class}`}>{op.type}</span>
                          <span>{op.description}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="help-card">
              <h3 className="card-title">
                <FontAwesomeIcon icon={faFilter} /> Filtering Operations
              </h3>
              <div className="card-content">
                <p>Use the filter controls to focus on specific data:</p>
                <ol className="help-steps">
                  <li>
                    <strong>Select a Crane</strong>
                    <p>Choose a specific crane or view all cranes</p>
                    <code>Filter → Crane → Select from dropdown</code>
                  </li>
                  <li>
                    <strong>Choose Operation Type</strong>
                    <p>Filter by hoist, travel, or switching operations</p>
                    <code>Filter → Operation Type → Select type</code>
                  </li>
                  <li>
                    <strong>Set Date Range</strong>
                    <p>View today's operations or historical data</p>
                    <code>Filter → Date Range → Choose period</code>
                  </li>
                  <li>
                    <strong>Apply Filters</strong>
                    <p>Click "Apply Filters" to update the view</p>
                    <code>Click "Apply Filters" button</code>
                  </li>
                </ol>
              </div>
            </div>

            <div className="help-card">
              <h3 className="card-title">
                <FontAwesomeIcon icon={faCogs} /> Advanced Features
              </h3>
              <div className="card-content">
                <div className="feature-list">
                  {advancedFeatures.map((feature, index) => (
                    <div className="feature-item" key={index}>
                      <h4><FontAwesomeIcon icon={feature.icon} /> {feature.title}</h4>
                      <p>{feature.description}</p>
                      <code>{feature.code}</code>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <div className="help-card troubleshooting">
              <h3 className="card-title">
                <FontAwesomeIcon icon={faWrench} /> Troubleshooting
              </h3>
              <div className="card-content">
                {troubleshootingItems.map((item, index) => (
                  <div className="troubleshooting-item" key={index}>
                    <h4>{item.title}</h4>
                    <ul>
                      {item.solutions.map((solution, idx) => (
                        <li key={idx}>{solution}</li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* FAQ Section */}
          <h2 className="section-title">
            <FontAwesomeIcon icon={faQuestionCircle} /> Frequently Asked Questions
          </h2>

          {faqItems.map((item, index) => (
            <div className="faq-item" key={index}>
              <div className="faq-question" onClick={() => toggleFaq(index)}>
                {item.question}
                <FontAwesomeIcon icon={faChevronDown} className={openFaqs[index] ? 'rotated' : ''} />
              </div>
              <div className={`faq-answer ${openFaqs[index] ? 'show' : ''}`}>
                {item.answer}
              </div>
            </div>
          ))}

          {/* Contact Support Section */}
          <h2 className="section-title">
            <FontAwesomeIcon icon={faHeadset} /> Contact Support
          </h2>

          <div className="contact-methods">
            {contactMethods.map((method, index) => (
              <div className="contact-card" key={index}>
                <div className="contact-icon">
                  <FontAwesomeIcon icon={method.icon} />
                </div>
                <h3 className="contact-title">{method.title}</h3>
                <p className="contact-detail">{method.detail}</p>
                <p>{method.description}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Notification */}
      {notification && (
        <div className={`notification notification-${notification.type} show`}>
          <FontAwesomeIcon icon={notification.type === 'success' ? faCheckCircle : faExclamationCircle} />
          {notification.message}
        </div>
      )}
    </div>
  );
};

export default Help;